import { useReactiveVar } from '@apollo/client';
import usePagesAvailability from 'hooks/usePagesAvailability/usePagesAvailability';
import useTranslation from 'hooks/useTranslation/useTranslation';
import AccessSettingsPage from 'pages/Access/AccessMode/AccessSettings';
import AccessProfiles from 'pages/Access/AccessProfiles/AccessProfiles';
import SchedulesPage from 'pages/Access/SchedulesPage/SchedulesPage';
import AccessEventsPage from 'pages/Events/AccessEvents/AccessEvents';
import CardsPage from 'pages/Hardware/Cards/Cards';
import LocksWithGroupsPage from 'pages/Hardware/Locks/Locks';
import MobileNotSupportedPage from 'pages/MobileNotSupported/MobileNotSupported';
import AccessAppPage from 'pages/MyProfile/AccessApp/AccessApp';
import SecurityPage from 'pages/MyProfile/Security/Security';
import RegistrationPage from 'pages/Registration/Registration';
import ApiKeysPage from 'pages/Settings/ApiKeys/ApiKeys';
import PermissionPage from 'pages/Settings/Permission/Permission';
import ExternalUsersPage from 'pages/Users/ExternalUsers/ExternalUsers';
import InstallersPage from 'pages/Users/Installers/Installers';
import React from 'react';
import { isMobile } from 'react-device-detect';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { authVar } from 'state/vars';
import Layout from 'ui/templates/Layout/Layout';

const handleNavigation = (isAdmin: boolean, isMobileDeviceRegistered: boolean) => {
  if (isMobile) return '/mobile-page-not-supported';
  if (isAdmin) return '/events/access-events';
  if (isMobileDeviceRegistered) return '/my-profile/my-device';
  return '/registration';
};

const handleRegistrationRedirect = () => <RegistrationPage />;

const handleEventsAccessEventsRedirect = () => <AccessEventsPage />;
const handleEventsRedirect = () => <Navigate to="/events/access-events" />;

const handleSettingsPermissionRedirect = () => <PermissionPage />;
const handleSettingsApiKeysRedirect = () => <ApiKeysPage />;
const handleSettingsRedirect = (isEntraLess: boolean) => (
  <Navigate to={isEntraLess ? '/settings/api-keys' : '/settings/permission'} />
);

const handleHardwareLocksRedirect = () => <LocksWithGroupsPage />;
const handleHardwareCardsRedirect = () => <CardsPage />;
const handleHardwareRedirect = () => <Navigate to="/hardware/locks" />;

const handleAccessAccessSettingsRedirect = () => <AccessSettingsPage />;
const handleAccessAccessProfilesRedirect = () => <AccessProfiles />;
const handleAccessSchedulesRedirect = () => <SchedulesPage />;
const handleAccessRedirect = () => <Navigate to="/access/access-profiles" />;

const handleUsersExternalGroupsRedirect = () => <ExternalUsersPage />;
const handleUsersInstallersRedirect = () => <InstallersPage />;
const handleUsersRedirect = () => <Navigate to="/users/external-groups" />;

const handleMyProfileMyDeviceRedirect = () => <AccessAppPage />;

const handleMyProfileSecurityRedirect = (isEntraLess: boolean) =>
  isEntraLess ? <SecurityPage /> : <Navigate to="my-profile/my-device" />;
const handleMyProfileRedirect = () => <Navigate to="my-profile/my-device" />;

const handleMobileReportsRedirect = () => <RegistrationPage />;
const handleMobileMobileNotSupportedRedirect = () => <MobileNotSupportedPage />;

const Dashboard: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const location = useLocation();
  const { user } = useReactiveVar(authVar);
  const isAdmin = user?.isAdmin!; // TODO: Nested destruction not work, dont know why
  const isEntraLess = user?.isEntraLess!;
  const mobileDevices = user?.mobileDevices!;
  const {
    isRegistrationAvailable,
    isEventsAvailable,
    isSettingsAvailable,
    isPermissionAvailable,
    isHardwareAvailable,
    isCardsAvailable,
    isAccessAvailable,
    isUsersAvailable,
    isMyProfileAvailable,
    isSecurityAvailable,
    isMobileRegistrationAvailable,
    isMobileMobileNotSupportedAvailable
  } = usePagesAvailability({ translation, location });

  const handleShowSideNav = () => {
    if (location.pathname.includes('registration') || isMobile) return false;
    return true;
  };

  return (
    <Layout showSideNav={handleShowSideNav()}>
      {!isMobile ? (
        <Routes>
          {isRegistrationAvailable() && <Route path="/registration" element={handleRegistrationRedirect()} />}

          {isEventsAvailable() && (
            <Route path="/events">
              <Route path="/events/access-events" element={handleEventsAccessEventsRedirect()} />
              <Route path="/events" element={handleEventsRedirect()} />
            </Route>
          )}

          {isSettingsAvailable() && (
            <Route path="/settings">
              {isPermissionAvailable() && (
                <Route path="/settings/permission" element={handleSettingsPermissionRedirect()} />
              )}
              <Route path="/settings/api-keys" element={handleSettingsApiKeysRedirect()} />
              <Route path="/settings" element={handleSettingsRedirect(isEntraLess)} />
            </Route>
          )}

          {isHardwareAvailable() && (
            <Route path="/hardware">
              <Route path="/hardware/locks" element={handleHardwareLocksRedirect()} />
              {isCardsAvailable() && <Route path="/hardware/cards" element={handleHardwareCardsRedirect()} />}
              <Route path="/hardware" element={handleHardwareRedirect()} />
            </Route>
          )}

          {isAccessAvailable() && (
            <Route path="/access">
              <Route path="/access/access-profiles" element={handleAccessAccessProfilesRedirect()} />
              <Route path="/access/schedules" element={handleAccessSchedulesRedirect()} />
              <Route path="/access/access-settings" element={handleAccessAccessSettingsRedirect()} />
              <Route path="/access" element={handleAccessRedirect()} />
            </Route>
          )}

          {isUsersAvailable() && (
            <Route path="/users">
              <Route path="/users/external-groups" element={handleUsersExternalGroupsRedirect()} />
              <Route path="/users/installers" element={handleUsersInstallersRedirect()} />
              <Route path="/users" element={handleUsersRedirect()} />
            </Route>
          )}

          {isMyProfileAvailable() && (
            <Route path="/my-profile">
              <Route path="/my-profile/my-device" element={handleMyProfileMyDeviceRedirect()} />
              {isSecurityAvailable() && (
                <Route path="/my-profile/security" element={handleMyProfileSecurityRedirect(isEntraLess)} />
              )}
              <Route path="/my-profile" element={handleMyProfileRedirect()} />
            </Route>
          )}
          <Route
            path="*"
            element={
              <Navigate
                to={handleNavigation(
                  isAdmin,
                  mobileDevices?.some((device) => device.isRegistered)
                )}
              />
            }
          />
        </Routes>
      ) : (
        <Routes>
          {isMobileRegistrationAvailable() && <Route path="/registration" element={handleMobileReportsRedirect()} />}
          {isMobileMobileNotSupportedAvailable() && (
            <Route path="/mobile-page-not-supported" element={handleMobileMobileNotSupportedRedirect()} />
          )}
          <Route
            path="*"
            element={
              <Navigate
                to={handleNavigation(
                  isAdmin,
                  mobileDevices?.some((device) => device.isRegistered)
                )}
              />
            }
          />
        </Routes>
      )}
    </Layout>
  );
};

export default Dashboard;
